import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/InputDefault';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import IconProvider from '@/components/ui/IconProvider';
import { Save, Loader2, CheckCircle, Cloud, Globe } from 'lucide-react';
import { useExportVersions, SaveVersionParams } from './useExportVersions';

interface SaveVersionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  exportBlob: Blob;
  exportFilename: string;
  gameId: string;
  gameName: string;
  companyId: string;
  platform: string;
  templateName?: string;
  metadata?: Record<string, any>;
  onSaved?: () => void;
}

export function SaveVersionDialog({
  open,
  onOpenChange,
  exportBlob,
  exportFilename,
  gameId,
  gameName,
  companyId,
  platform,
  templateName,
  metadata,
  onSaved,
}: SaveVersionDialogProps) {
  const [versionName, setVersionName] = useState(() => {
    // Generate default version name with timestamp
    const date = new Date();
    const dateStr = date.toISOString().split('T')[0];
    const timeStr = date.toTimeString().split(' ')[0].replace(/:/g, '-');
    return `${gameName}-${platform}-${dateStr}-${timeStr}`;
  });
  const [isPublic, setIsPublic] = useState(true);
  const [saveComplete, setSaveComplete] = useState(false);
  
  const { toast } = useToast();
  const { saveVersion, isSaving } = useExportVersions();

  const handleSave = async () => {
    if (!versionName.trim()) {
      toast({
        title: 'Version name required',
        description: 'Please enter a name for this version',
        variant: 'destructive',
      });
      return;
    }

    const params: SaveVersionParams = {
      gameId,
      gameName,
      companyId,
      versionName: versionName.trim(),
      platform,
      templateName,
      metadata,
      isPublic,
      file: exportBlob,
      filename: exportFilename,
    };

    const result = await saveVersion(params);

    if (result) {
      setSaveComplete(true);
      toast({
        title: 'Version saved',
        description: `"${versionName}" has been saved to the cloud`,
      });

      // Close dialog after a brief delay
      setTimeout(() => {
        onOpenChange(false);
        setSaveComplete(false);
        onSaved?.();
      }, 1500);
    } else {
      toast({
        title: 'Save failed',
        description: 'Failed to save version to the cloud',
        variant: 'destructive',
      });
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!isSaving) {
      onOpenChange(newOpen);
      if (!newOpen) {
        setSaveComplete(false);
      }
    }
  };

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[450px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Cloud className="h-5 w-5 text-primary" />
            Save to Cloud
          </DialogTitle>
          <DialogDescription>
            Enter a name for this export version.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Export Info */}
          <div className="bg-muted/50 rounded-lg p-3 space-y-1 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">File:</span>
              <span className="font-mono text-xs truncate max-w-[200px]">{exportFilename}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Size:</span>
              <span>{formatFileSize(exportBlob.size)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Platform:</span>
              <span className="capitalize">{platform}</span>
            </div>
          </div>

          {/* Version Name Input */}
          <div className="space-y-2">
            <Label htmlFor="version-name">Version Name</Label>
            <Input
              id="version-name"
              value={versionName}
              onChange={(e) => setVersionName(e.target.value)}
              placeholder="Enter a name for this version"
              disabled={isSaving}
              autoFocus
              className="w-full"
            />
          </div>

          {/* Public Toggle */}
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div className="space-y-0.5">
              <div className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-muted-foreground" />
                <Label htmlFor="is-public" className="font-medium">
                  API Access
                </Label>
              </div>
              <p className="text-xs text-muted-foreground">
                Allow download via API key
              </p>
            </div>
            <Switch
              id="is-public"
              checked={isPublic}
              onCheckedChange={setIsPublic}
              disabled={isSaving}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving || saveComplete || !versionName.trim()}
            className="gap-2"
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : saveComplete ? (
              <>
                <CheckCircle className="h-4 w-4" />
                Saved!
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                Save
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
